<template>
    <validation-observer ref="form" v-slot="{ invalid }">
        <modal
            :id="id"
            :title="
                isView ? 'Submitted Guest Review' : step === 0 ? 'Submit Guest Review' : 'Confirm Guest Review'
            "
            :size="step === 0 ? 'lg' : 'md'"
            @hidden="$emit('hidden')"
        >
            <busy-modal :show="busy"></busy-modal>
            <span v-if="step === 0 && !isView">
                <h5>Leave a public review</h5>
                <validation-provider
                    name="publicReview"
                    v-slot="{ errors }"
                    rules="required|minMessageLen:6,Review"
                    
                >
                    <form-textarea
                        v-model="publicReview"
                        :label="publicReviewLabel"
                        placeholder="Say a few words about guest stay"
                        :error="errors[0]"
                        rows="3"
                    />
                </validation-provider>
                <h5>Add a private note</h5>
                <form-textarea
                    v-model="privateFeedback"
                    :label="privateFeedbackLabel"
                    placeholder="Add a private note (optional)"
                    rows="3"
                />
                <h5>Cleanliness, Communication and House Rules</h5>
                <div class="mt-n2">
                    <div class="mt-3">
                        <span>Did guest leave your space clean?</span>
                        <star-rating class="my-2" v-model="cleanliness" />
                        <div v-if="cleanliness > 0">
                            <div v-show="isPositiveCleanliness">
                                <b-button-group
                                    size="sm"
                                    class="ml-n2 mt-n2 flex-wrap"
                                    v-if="cleanlinessTags.positiveTags.length > 0"
                                >
                                    <b-button
                                        v-for="(tag, id) in cleanlinessTags.positiveTags"
                                        :key="id"
                                        pill
                                        :pressed="tag.state"
                                        class="tag ml-2 mt-2 flex-grow-0"
                                        :class="{'active': tag.state}"
                                        variant="outline-success"
                                         @click="tag.state = !tag.state"
                                    >
                                        {{ tag.label }}
                                    </b-button>
                                </b-button-group>
                            </div>
                            <div v-show="!isPositiveCleanliness">
                                <b-button-group
                                    size="sm"
                                    class="ml-n2 mt-n2 flex-wrap"
                                    v-if="cleanlinessTags.negativeTags.length > 0"
                                >
                                    <b-button
                                        v-for="(tag, id) in cleanlinessTags.negativeTags"
                                        :key="id"
                                        pill
                                        :pressed="tag.state"
                                        class="tag ml-2 mt-2 flex-grow-0"
                                        :class="{'active': tag.state}"
                                        variant="outline-danger"
                                         @click="tag.state = !tag.state"
                                    >
                                        {{ tag.label }}
                                    </b-button>
                                </b-button-group>
                            </div>
                        </div>
                    </div>
                    <div class="mt-3">
                        <span> How clearly did guest communicate their plans, questions, and concerns? </span>
                        <star-rating class="my-2" v-model="communication" />
                        <div v-if="communication > 0">
                            <div v-show="isPositiveCommunication">
                                <b-button-group
                                    size="sm"
                                    class="ml-n2 mt-n2 flex-wrap"
                                    v-if="communicationTags.positiveTags.length > 0"
                                >
                                    <b-button
                                        v-for="(tag, id) in communicationTags.positiveTags"
                                        :key="id"
                                        pill
                                        :pressed="tag.state"
                                        class="tag ml-2 mt-2 flex-grow-0"
                                        :class="{'active': tag.state}"
                                        variant="outline-success"
                                         @click="tag.state = !tag.state"
                                    >
                                        {{ tag.label }}
                                    </b-button>
                                </b-button-group>
                            </div>
                            <div v-show="!isPositiveCommunication">
                                <b-button-group
                                    size="sm"
                                    class="ml-n2 mt-n2 flex-wrap"
                                    v-if="communicationTags.negativeTags.length > 0"
                                >
                                    <b-button
                                        v-for="(tag, id) in communicationTags.negativeTags"
                                        :key="id"
                                        pill
                                        :pressed="tag.state"
                                        class="tag ml-2 mt-2 flex-grow-0"
                                        :class="{'active': tag.state}"
                                        variant="outline-danger"
                                         @click="tag.state = !tag.state"
                                    >
                                        {{ tag.label }}
                                    </b-button>
                                </b-button-group>
                            </div>
                        </div>
                    </div>
                    <div class="mt-3">
                        <span>Did guest observe your house rules?</span>
                        <star-rating class="my-2" v-model="respectHouseRules" />
                        <div v-if="respectHouseRules > 0">
                            <div v-show="isPositiveRespectHouseRules">
                                <b-button-group
                                    size="sm"
                                    class="ml-n2 mt-n2 flex-wrap"
                                    v-if="respectHouseRulesTags.positiveTags.length > 0"
                                >
                                    <b-button
                                        v-for="(tag, id) in respectHouseRulesTags.positiveTags"
                                        :key="id"
                                        pill
                                        :pressed="tag.state"
                                        class="tag ml-2 mt-2 flex-grow-0"
                                        :class="{'active': tag.state}"
                                        variant="outline-success"
                                        @click="tag.state = !tag.state"
                                    >
                                        {{ tag.label }}
                                    </b-button>
                                </b-button-group>
                            </div>
                            <div v-show="!isPositiveRespectHouseRules">
                                <b-button-group
                                    size="sm"
                                    class="ml-n2 mt-n2 flex-wrap"
                                    v-if="respectHouseRulesTags.negativeTags.length > 0"
                                >
                                    <b-button
                                        v-for="(tag, id) in respectHouseRulesTags.negativeTags"
                                        :key="id"
                                        pill
                                        :pressed="tag.state"
                                        class="tag ml-2 mt-2 flex-grow-0"
                                        :class="{'active': tag.state}"
                                        variant="outline-danger"
                                        @click="tag.state = !tag.state"
                                    >
                                        {{ tag.label }}
                                    </b-button>
                                </b-button-group>
                            </div>
                        </div>
                    </div>
                </div>
                <h6 class="mt-4 mb-2">
                    Would you host guest again? We won't publish your answer anywhere, and guest won't see it.
                </h6>
                <span>
                    <b-button
                        @click="isRevieweeRecommended = false"
                        variant="outline-primary"
                        :class="{
                            active: isRevieweeRecommended === false,
                        }"
                    >
                        No
                    </b-button>
                    <b-button
                        @click="isRevieweeRecommended = true"
                        variant="outline-primary"
                        :class="{
                            active: isRevieweeRecommended === true,
                        }"
                    >
                        Yes
                    </b-button>
                </span>
            </span>
            <span v-else>
                <h5>Public Review</h5>
                <div :class="privateFeedback ? 'pb-3' : ''">
                    {{ publicReview }}
                </div>
                <h5 v-if="privateFeedback">Private Note</h5>
                <div class="pb-3">{{ privateFeedback }}</div>
                <h5>Ratings</h5>
                <div class="d-flex pb-2">
                    <span class="pr-1">Cleanliness:</span>
                    <star-rating v-model="cleanliness" :read-only="true" />
                </div>
                <ul class="tag-list" v-if="selectedCleanlinessTags.length > 0">
                    <li v-for="(tag, id) in selectedCleanlinessTags" :key="id">
                        <div :class="tag.positive ? 'text-success' : 'text-danger'">
                            <span class="tag-list__icon">
                                <font-awesome-icon
                                    :icon="['fas', tag.positive ? 'circle-plus' : 'minus-circle']"
                                ></font-awesome-icon>
                            </span>
                            {{ tag.label }}
                        </div>
                    </li>
                </ul>
                <div class="d-flex pb-2">
                    <span class="pr-1">Communication:</span>
                    <star-rating v-model="communication" :read-only="true" />
                </div>
                <ul class="tag-list" v-if="selectedCommunicationTags.length > 0">
                    <li v-for="(tag, id) in selectedCommunicationTags" :key="id">
                        <div :class="tag.positive ? 'text-success' : 'text-danger'">
                            <span class="tag-list__icon">
                                <font-awesome-icon
                                    :icon="['fas', tag.positive ? 'circle-plus' : 'minus-circle']"
                                ></font-awesome-icon>
                            </span>
                            {{ tag.label }}
                        </div>
                    </li>
                </ul>
                <div class="d-flex" :class="selectedRespectHouseRulesTags.length > 0 ? 'pb-2' : 'pb-3'">
                    <span class="pr-1">House Rules:</span>
                    <star-rating v-model="respectHouseRules" :read-only="true" />
                </div>
                <ul class="tag-list" v-if="selectedRespectHouseRulesTags.length > 0">
                    <li v-for="(tag, id) in selectedRespectHouseRulesTags" :key="id">
                        <div :class="tag.positive ? 'text-success' : 'text-danger'">
                            <span class="tag-list__icon">
                                <font-awesome-icon
                                    :icon="['fas', tag.positive ? 'circle-plus' : 'minus-circle']"
                                ></font-awesome-icon>
                            </span>
                            {{ tag.label }}
                        </div>
                    </li>
                </ul>
                <div>
                    <b>Would you host guest again:</b>
                    {{ isRevieweeRecommended ? 'Yes' : 'No' }}
                </div>
            </span>
            <template #modal-footer="{ cancel }">
                <div class="w-100">
                    <b-button v-show="step === 1" class="float-left" variant="outline-primary" @click="step--">
                        Back
                    </b-button>
                    <b-button
                        v-show="!isView"
                        class="float-right ml-2"
                        variant="primary"
                        @click="step === 0 ? step++ : submitGuestReview()"
                        :disabled="invalid || ratingsIsNotSelected || isRevieweeRecommended === null"
                    >
                        {{ step === 0 ? 'Submit Review' : 'Confirm' }}
                    </b-button>
                    <b-button class="float-right" :variant="isView ? 'primary' : 'secondary'" @click="cancel()">
                        Close
                    </b-button>
                </div>
            </template>
        </modal>
    </validation-observer>
</template>

<script>
import BusyModal from '../generic/busy-modal/busy-modal.vue';
import FormTextarea from '../generic/form-textarea/form-textarea.vue';
import Modal from '../generic/modal/modal.vue';
import StarRating from '../generic/star-rating/star-rating.vue';

export default {
    components: { BusyModal, FormTextarea, Modal, StarRating },
    props: {
        busy: {
            type: Boolean,
            default: false,
        },
        id: {
            type: String,
            default: null,
        },
        isView: {
            type: Boolean,
            default: false,
        },
        review: {
            type: Object,
            default: null,
        },
        tags: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            publicReviewLabel: `Write a fair, honest review about guest's stay so future hosts know what to expect.`,
            privateFeedbackLabel: `Offer suggestions, or say thanks for being a great guest.
                 We won't publish your note on guest's profile.`,
            step: 0,
            publicReview: null,
            privateFeedback: null,
            cleanliness: null,
            communication: null,
            respectHouseRules: null,
            isRevieweeRecommended: null,
            cleanlinessTags: null,
            communicationTags: null,
            respectHouseRulesTags: null,
        };
    },
    computed: {
        ratingsIsNotSelected() {
            return !this.cleanliness || !this.communication || !this.respectHouseRules;
        },
        selectedCleanlinessTags() {
            if (this.isView) {
                return this.cleanlinessTags;
            } else {
                if (this.isPositiveCleanliness) {
                    return this.cleanlinessTags?.positiveTags.filter((tag) => tag.state) || [];
                }
                return this.cleanlinessTags?.negativeTags.filter((tag) => tag.state) || [];
            }
        },
        selectedCommunicationTags() {
            if (this.isView) {
                return this.communicationTags;
            } else {
                if (this.isPositiveCommunication) {
                    return this.communicationTags?.positiveTags.filter((tag) => tag.state) || [];
                }
                return this.communicationTags?.negativeTags.filter((tag) => tag.state) || [];
            }
        },
        selectedRespectHouseRulesTags() {
            if (this.isView) {
                return this.respectHouseRulesTags;
            } else {
                if (this.isPositiveRespectHouseRules) {
                    return this.respectHouseRulesTags?.positiveTags.filter((tag) => tag.state) || [];
                }
                return this.respectHouseRulesTags?.negativeTags.filter((tag) => tag.state) || [];
            }
        },
        isPositiveCleanliness() {
            return this.cleanliness >= 4;
        },
        isPositiveCommunication() {
            return this.communication >= 4;
        },
        isPositiveRespectHouseRules() {
            return this.respectHouseRules >= 4;
        },
    },
    created() {
        this.publicReview = this.review?.public_review || '';
        this.privateFeedback = this.review?.private_feedback || '';
        this.cleanliness = this.review?.cleanliness || 0;
        this.communication = this.review?.communication || 0;
        this.respectHouseRules = this.review?.respect_house_rules || 0;
        this.isRevieweeRecommended = this.review?.is_reviewee_recommended || null;
        if (this.isView) {
            this.cleanlinessTags = this.review.tags?.cleanliness_tags || [];
            this.communicationTags = this.review.tags?.communication_tags || [];
            this.respectHouseRulesTags = this.review.tags?.respect_house_rules_tags || [];
        } else {
            this.cleanlinessTags = this.generateTags(this.tags?.cleanliness_tags);
            this.communicationTags = this.generateTags(this.tags?.communication_tags);
            this.respectHouseRulesTags = this.generateTags(this.tags?.respect_house_rules_tags);
        }
    },
    methods: {
        getReviewTags() {
            return [
                ...this.selectedCleanlinessTags.map((tag) => {
                    return tag.value;
                }),
                ...this.selectedCommunicationTags.map((tag) => {
                    return tag.value;
                }),
                ...this.selectedRespectHouseRulesTags.map((tag) => {
                    return tag.value;
                }),
            ];
        },
        generateTags(tags) {
            return (
                tags?.reduce(
                    (result, item) => {
                        const tag = {
                            label: item.label,
                            value: item.value,
                            positive: item.positive,
                            state: false,
                        };
                        if (tag.positive) {
                            result.positiveTags.push(tag);
                        } else {
                            result.negativeTags.push(tag);
                        }
                        return result;
                    },
                    { positiveTags: [], negativeTags: [] }
                ) || { positiveTags: [], negativeTags: [] }
            );
        },
        submitGuestReview() {
            this.$emit('submit-guest-review', {
                publicReview: this.publicReview,
                privateFeedback: this.privateFeedback,
                cleanliness: this.cleanliness,
                communication: this.communication,
                respectHouseRules: this.respectHouseRules,
                isRevieweeRecommended: this.isRevieweeRecommended,
                tags: this.getReviewTags(),
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.tag:focus {
    box-shadow: none !important;
}

.tag-list {
    padding-left: 20px;
    list-style-type: none;

    &__icon {
        padding-right: 10px;
    }
}
</style>
